import React, { useState } from "react";
import "./EditProfileModal.scss";
import Modal from "../Modal/Modal";
import { ModalFooter, ModalButton } from "../Modal/ModalLayout";
import UserService, { IUpdateProfileRequest } from "../../services/UserService";
import { IUser } from "../../interfaces/user.interface";

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  userData: IUser;
  onProfileUpdated: (updatedUser: IUser) => void;
}

const EditProfileModal: React.FC<EditProfileModalProps> = ({
  isOpen,
  onClose,
  userData,
  onProfileUpdated,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    userFullName: userData.userFullName,
    phoneNumber: userData.phoneNumber,
    address: userData.address || "",
    city: userData.city || "",
    state: userData.state || "",
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  if (!isOpen) return null;

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.userFullName.trim()) {
      newErrors.userFullName = "Full Name is required";
    } else if (formData.userFullName.length < 2) {
      newErrors.userFullName = "Name must be at least 2 characters";
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (!/^[0-9]{10}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = "Phone must be exactly 10 digits";
    }

    if (formData.address.trim() && formData.address.length < 10) {
      newErrors.address = "Address must be at least 10 characters";
    }

    if (formData.city.trim() && formData.city.length < 2) {
      newErrors.city = "City must be at least 2 characters";
    }

    if (formData.state.trim() && formData.state.length < 2) {
      newErrors.state = "State must be at least 2 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const isFormValid = () => {
    return (
      formData.userFullName.trim() &&
      formData.phoneNumber.trim() &&
      /^[0-9]{10}$/.test(formData.phoneNumber) &&
      Object.keys(errors).length === 0
    );
  };

  const isFormDirty = () => {
    return (
      formData.userFullName !== userData.userFullName ||
      formData.phoneNumber !== userData.phoneNumber ||
      formData.address !== (userData.address || "") ||
      formData.city !== (userData.city || "") ||
      formData.state !== (userData.state || "")
    );
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      const updateData: IUpdateProfileRequest = {
        userFullName: formData.userFullName,
        phoneNumber: formData.phoneNumber,
        address: formData.address || undefined,
        city: formData.city || undefined,
        state: formData.state || undefined,
      };

      const updatedUser = await UserService.updateProfile(updateData);

      // Refresh profile data after successful update
      try {
        const refreshedUser = await UserService.getMyProfile();
        onProfileUpdated(refreshedUser);
      } catch (refreshError) {
        console.error("Error refreshing profile:", refreshError);
        onProfileUpdated(updatedUser);
      }

      onClose();
    } catch (error) {
      console.error("Error updating profile:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFooterButtons = () => {
    const isValid = isFormValid();
    const isDirty = isFormDirty();

    console.log("Form state:", { isValid, isDirty, formData, errors });

    return (
      <ModalFooter>
        <ModalButton variant="secondary" onClick={onClose}>
          Cancel
        </ModalButton>
        <ModalButton
          variant="primary"
          onClick={handleSubmit}
          disabled={!isValid || !isDirty || isSubmitting}
        >
          {isSubmitting ? "Updating..." : "Update"}
        </ModalButton>
      </ModalFooter>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Profile"
      size="lg"
      closeOnModalOverlayClick={true}
      footer={getFooterButtons()}
    >
      <div className="edit-profile-modal">
        {/* Personal Information Section */}
        <div className="form-section">
          <h3 className="section-title">Personal Information</h3>

          <div className="form-group">
            <div className="form-field">
              <label htmlFor="userFullName" className="required_field_label">
                Full Name
              </label>
              <input
                type="text"
                id="userFullName"
                value={formData.userFullName}
                onChange={(e) =>
                  handleInputChange("userFullName", e.target.value)
                }
                placeholder="Enter your full name"
              />
            </div>
            {errors.userFullName && (
              <div className="validation_error">{errors.userFullName}</div>
            )}
          </div>

          <div className="form-group">
            <div className="form-field">
              <label htmlFor="phoneNumber" className="required_field_label">
                Phone Number
              </label>
              <input
                maxLength={10}
                type="tel"
                id="phoneNumber"
                value={formData.phoneNumber}
                onChange={(e) =>
                  handleInputChange("phoneNumber", e.target.value)
                }
                placeholder="Enter your phone number"
              />
            </div>
            {errors.phoneNumber && (
              <div className="validation_error">{errors.phoneNumber}</div>
            )}
          </div>
        </div>

        {/* Address Section */}
        {/* <div className="form-section">
          <h3 className="section-title">Address Information</h3>

          <div className="form-group">
            <div className="form-field">
              <label htmlFor="address">Address</label>
              <textarea
                id="address"
                value={formData.address}
                onChange={(e) => handleInputChange("address", e.target.value)}
                placeholder="Enter your address"
                rows={3}
                className="form-textarea"
              />
            </div>
            {errors.address && (
              <div className="validation_error">{errors.address}</div>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <div className="form-field">
                <label htmlFor="city">City</label>
                <input
                  type="text"
                  id="city"
                  value={formData.city}
                  onChange={(e) => handleInputChange("city", e.target.value)}
                  placeholder="Enter your city"
                />
              </div>
              {errors.city && (
                <div className="validation_error">{errors.city}</div>
              )}
            </div>

            <div className="form-group">
              <div className="form-field">
                <label htmlFor="state">State</label>
                <input
                  type="text"
                  id="state"
                  value={formData.state}
                  onChange={(e) => handleInputChange("state", e.target.value)}
                  placeholder="Enter your state"
                />
              </div>
              {errors.state && (
                <div className="validation_error">{errors.state}</div>
              )}
            </div>
          </div>
        </div> */}
      </div>
    </Modal>
  );
};

export default EditProfileModal;
